/**
 * Personnel › « Classement » — le classement de la semaine des chatteurs.
 *
 * Kayla, 07-10-2026 : « une sorte de leaderboard de la semaine, calculé par
 * rapport à celui qui a fait le plus d'argent » ; « qu'ils aient tous accès au
 * leaderboard : on voit qui a fait combien et à quelle position ils sont ».
 * Les trois premiers touchent une prime le lundi à 12h — on montre la prime de
 * chaque place, qui grossit avec la semaine, jamais le pourcentage.
 *
 * 📐 Les DIMENSIONS sont celles des onglets frères (`AnalyticsTab`), pas les
 * siennes — Kayla, 07-10 soir, sur la v1 centrée dans une colonne étroite avec
 * un titre géant : « c'est pas les bonnes dimensions que mes autres pages ».
 * Donc : pleine largeur, `p-3 sm:p-4`, en-tête en petites capitales
 * (`text-[11px]`). La DISPOSITION, elle, reste celle qu'elle a validée : ma
 * place, le podium en haut, la liste en dessous.
 * ⚖️ Ex æquo (07-10) : même place, même prime — elle se lit sur chaque ligne
 * (`primeCents`), jamais sur `primes[rang − 1]`.
 * Et la page ne défile jamais : la liste prend la hauteur qui reste et se
 * PAGINE (Kayla, 07-10 : « s'il n'y a pas la place, une pagination »).
 *
 * Depuis le 27-10-2026, « ma place », le podium (scène, blocs de pierre,
 * trophée) et les lignes viennent de la librairie `@/shared/ui/classement` ;
 * l'écran garde ses règles (primes, ex æquo, semaine dernière, pagination sur
 * la hauteur, première page = la mienne) et ses dollars (« 248,40 $ »).
 *
 * La donnée : `classement.semaine` (Convex), un document que Convex relit chez
 * Cyrano à chaque paiement et toutes les 5 min — l'écran est en temps réel.
 * La vue (`ClassementVue`) est pure, pour le harnais `classement-dev.html`.
 */
import { useEffect, useMemo, useState } from "react";
import type { FunctionReturnType } from "convex/server";
import { LayoutGroup, motion } from "framer-motion";

import { api } from "@/../convex/_generated/api";
import type { Id } from "@/../convex/_generated/dataModel";
import { place } from "@/../convex/_lib/classement";
import { useContainerSize } from "@/core/hooks/useContainerSize";
import { cn } from "@/core/utils";
import { nextUnlockMs } from "@/panels/organisation/payoutCycle";
import { Badge, Carte, Etiquette, Infobulle, Pagination, Section, Vide } from "@dozka/ui";
import { HAUTEUR_LIGNE, MaPlace, Podium, RangCarte, type EntreeClassement } from "@dozka/ui/classement";
import { Loader2, Timer, Trophy } from "@/shared/ui/icons";

import { useClassementSemaine } from "./useClassementSemaine";

export type ClassementData = NonNullable<FunctionReturnType<typeof api.classement.semaine>>;
type LigneData = ClassementData["lignes"][number];

/** Les métaux — sourds, jamais le doré criard (Kayla a refusé « le jaune trop
 *  IA » sur la célébration de la Régie). Identité de la place : fond plein.
 *  Les mêmes valeurs que `--cl-metal-1/2/3` de la librairie. */
export const METAL: Record<number, string> = { 1: "#DEC480", 2: "#C4CAD4", 3: "#CC9670" };

export function ClassementOnglet({ accountId }: { accountId: Id<"accounts"> }) {
  const data = useClassementSemaine(accountId);
  return <ClassementVue data={data} />;
}

/** « 248 $ », « 9,92 $ » — les cents seulement quand il y en a : une prime de
 *  9,92 $ affichée « 9 $ » ment de presque un dollar (v1, 07-10). */
export function dollars(cents: number): string {
  const entier = Math.floor(cents / 100);
  const reste = Math.round(cents % 100);
  const base = entier.toLocaleString("fr-FR");
  return reste === 0 ? `${base} $` : `${base},${String(reste).padStart(2, "0")} $`;
}

/** Le même format, pour une valeur en dollars (ce que la librairie reçoit). */
const dollarsUnites = (valeur: number): string => dollars(Math.round(valeur * 100));

/** « 4 j 6 h », « 5 h 12 min », « 3 min » — ce qu'il reste avant lundi 12h. */
function resteAvant(ms: number): string {
  const min = Math.max(0, Math.floor(ms / 60_000));
  const j = Math.floor(min / 1440);
  const h = Math.floor((min % 1440) / 60);
  if (j > 0) return `${j} j ${h} h`;
  if (h > 0) return `${h} h ${min % 60} min`;
  return `${min} min`;
}

function useMaintenant(intervalleMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalleMs);
    return () => clearInterval(id);
  }, [intervalleMs]);
  return now;
}

/** La hauteur du podium suit celle du panneau : 47 %, entre 260 et 600 px —
 *  le podium est le héros ; sous 760 px, « ma place » passe en compact pour
 *  lui laisser la hauteur (Kayla, 27-10 : « les textes, la photo, tout petit »). */
function hauteurDuPodium(hauteurPanneau: number): number {
  return hauteurPanneau > 0 ? Math.round(Math.min(600, Math.max(260, hauteurPanneau * 0.42))) : 300;
}

export function ClassementVue({ data, now: nowFixe }: { data: ClassementData | null | undefined; now?: number }) {
  const nowVivant = useMaintenant(30_000);
  const now = nowFixe ?? nowVivant;
  const { ref: racine, size } = useContainerSize<HTMLDivElement>();
  const deuxColonnes = size.width >= 880;

  if (data === undefined) {
    return (
      <div className="flex h-full items-center justify-center text-muted">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  return (
    <div ref={racine} className="flex h-full min-h-0 flex-col gap-3 p-3 sm:p-4">
      {/* En-tête : comme les onglets frères — petites capitales à gauche,
          le compte à rebours à droite. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <Etiquette as="h3" icone={<Trophy className="h-3.5 w-3.5" />}>
            Classement de la semaine
          </Etiquette>
          <p className="mt-1 text-[13px] text-muted">Les 3 premiers gagnent une prime, versée lundi à 12h.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {data && data.semaineDerniere.length > 0 && <SemaineDerniere gagnants={data.semaineDerniere} />}
          <Badge ton="gris" size="md" icon={<Timer className="h-3.5 w-3.5" />}>
            Fin dans {resteAvant(nextUnlockMs(now) - now)}
          </Badge>
        </div>
      </div>

      {data === null ? (
        <Carte className="flex flex-1 items-center justify-center">
          <Vide icone={<Trophy />} titre="Le classement démarre avec la première vente de la semaine." />
        </Carte>
      ) : (
        <>
          <MaPlaceDeLaSemaine data={data} compact={size.height > 0 && size.height < 760} />
          {/* Panneau large : le podium à gauche sur toute la hauteur (photos et
              montants grands), l'équipe à droite avec six à huit lignes. Étroit :
              empilés, le podium à une hauteur calculée. */}
          <div
            className={
              deuxColonnes
                ? "grid min-h-0 flex-1 grid-cols-[3fr_2fr] grid-rows-1 gap-3"
                : "flex min-h-0 flex-1 flex-col gap-3"
            }
          >
            <PodiumDeLaSemaine data={data} hauteur={deuxColonnes ? "100%" : hauteurDuPodium(size.height)} />
            <Equipe lignes={data.lignes.slice(3)} />
          </div>
        </>
      )}
    </div>
  );
}

/** Une ligne Convex → une entrée de la librairie (cents → dollars, stats cachées → `null`). */
function entreeDe(l: LigneData): EntreeClassement {
  return {
    id: l.workosUserId,
    rang: l.rang,
    nom: l.nom,
    icone: l.avatarUrl ?? undefined,
    montant: l.brutCents === null ? null : l.brutCents / 100,
    estMoi: l.moi,
    exAequo: l.exAequo,
  };
}

// ─── Où j'en suis ────────────────────────────────────────────────────────────

function MaPlaceDeLaSemaine({ data, compact }: { data: ClassementData; compact: boolean }) {
  const moi = data.lignes.find((l) => l.moi);
  // Celui juste devant : la meilleure place encore au-dessus de la mienne
  // (avec des ex æquo, ce n'est pas forcément « mon rang − 1 »).
  const devant = moi ? data.lignes.filter((l) => l.rang < moi.rang).at(-1) : undefined;
  // Ce qu'il manque pour passer devant celui du dessus : connu seulement si
  // les deux montants le sont (un collègue peut avoir caché ses stats).
  const manque =
    moi && devant && moi.brutCents !== null && devant.brutCents !== null
      ? devant.brutCents - moi.brutCents + 100
      : null;

  let titre: string;
  let detail: string;
  if (!moi) {
    titre = "Pas encore classé";
    detail = "Fais ta première vente de la semaine pour entrer dans le classement.";
  } else if (moi.rang === 1) {
    titre = moi.exAequo ? "Tu es 1er ex æquo !" : "Tu es 1er !";
    detail = moi.exAequo
      ? `Vous êtes à égalité : la prime est partagée, ${dollars(moi.primeCents)} chacun. Une vente de plus et elle est pour toi.`
      : `Garde ta place jusqu'à lundi pour gagner ${dollars(moi.primeCents)}.`;
  } else {
    titre = `Tu es ${place(moi.rang)}${moi.exAequo ? " ex æquo" : ""}`;
    const visee = devant?.rang ?? moi.rang - 1;
    detail =
      manque !== null
        ? `Encore ${dollars(manque)} de ventes pour ${visee === 1 ? "prendre la 1re place" : `passer ${place(visee)}`}${
            visee <= 3 ? ` et viser ${dollars(data.primes[visee - 1])}` : ""
          }.`
        : moi.primeCents > 0
          ? `Garde ta place jusqu'à lundi pour gagner ${dollars(moi.primeCents)}.`
          : "Continue, le podium n'est pas loin.";
  }

  return (
    <MaPlace
      className="shrink-0"
      compact={compact}
      rang={moi?.rang ?? null}
      titre={titre}
      detail={detail}
      montant={moi && moi.brutCents !== null ? moi.brutCents / 100 : undefined}
      etiquetteMontant="Vendu cette semaine"
      formater={dollarsUnites}
    />
  );
}

// ─── Le podium ───────────────────────────────────────────────────────────────

/** La prime sous chaque marche — celle de la personne (ex æquo : partagée), ou
 *  celle de la place quand elle est à prendre. */
function PrimeDeLaMarche({ cents, aPrendre }: { cents: number; aPrendre: boolean }) {
  return (
    <span className="flex items-baseline justify-center gap-1 whitespace-nowrap">
      <span className="cl-etiquette text-[0.8em] opacity-60">Prime</span>
      <span className="cl-montant font-semibold">{dollars(cents)}</span>
      {aPrendre && <span className="opacity-60">· À prendre</span>}
    </span>
  );
}

function PodiumDeLaSemaine({ data, hauteur }: { data: ClassementData; hauteur: number | string }) {
  // Les trois MARCHES sont les trois premières lignes ; le chiffre gravé est le
  // rang de la personne — deux ex æquo 1ers occupent les marches 1 et 2, tous
  // deux « 1 », avec la même prime partagée.
  const parId = new Map(data.lignes.map((l) => [l.workosUserId, l]));
  // Empilé (panneau étroit) : la scène ne s'étire pas sur toute la largeur —
  // elle se centre à la largeur de son format (Kayla, 27-10 : « quand il prend
  // toute la longueur, c'est horrible »). En colonne (`"100%"`), elle remplit.
  const largeurMax = typeof hauteur === "number" ? Math.round((hauteur * 1000) / 700) : undefined;
  return (
    <Podium
      className="mx-auto min-h-0 shrink-0"
      largeurMax={largeurMax}
      hauteur={hauteur}
      entrees={data.lignes.slice(0, 3).map(entreeDe)}
      formater={dollarsUnites}
      suffixe=" vendus"
      sousMarche={(marche, entree) => {
        const ligne = entree ? parId.get(entree.id ?? "") : undefined;
        return (
          <PrimeDeLaMarche cents={ligne ? ligne.primeCents : (data.primes[marche - 1] ?? 0)} aPrendre={!ligne} />
        );
      }}
    />
  );
}

// ─── Le reste de l'équipe ────────────────────────────────────────────────────

/** Hauteur d'une ligne de l'équipe (px) — sert à compter combien tiennent
 *  sans défiler, comme la table de « Mes ventes ». */
const LIGNE_PX = HAUTEUR_LIGNE.dense;

/**
 * L'équipe, PAGINÉE : jamais de défilement (Kayla, 07-10 : « s'il n'y a pas la
 * place, une pagination pour voir le reste du classement »). Le nombre de
 * lignes par page se règle sur la hauteur mesurée ; la première page montrée
 * est celle où je suis.
 */
function Equipe({ lignes }: { lignes: LigneData[] }) {
  const [corps, setCorps] = useState<HTMLDivElement | null>(null);
  const [parPage, setParPage] = useState(6);
  useEffect(() => {
    if (!corps) return;
    // Jamais une ligne coupée : ce qui tient, et rien de plus (au moins une).
    const ro = new ResizeObserver(([e]) => setParPage(Math.max(1, Math.floor(e!.contentRect.height / LIGNE_PX))));
    ro.observe(corps);
    return () => ro.disconnect();
  }, [corps]);

  const pages = Math.max(1, Math.ceil(lignes.length / parPage));
  const maPage = Math.floor(Math.max(0, lignes.findIndex((l) => l.moi)) / parPage) + 1;
  const [choisie, setChoisie] = useState<number | null>(null);
  const page = Math.min(choisie ?? maPage, pages);
  const visibles = lignes.slice((page - 1) * parPage, page * parPage);

  return (
    <Section
      titre="Le reste de l'équipe"
      remplissage="aucun"
      className="flex h-full min-h-[8rem] flex-1 flex-col overflow-hidden"
      classNameCorps="flex min-h-0 flex-1 flex-col"
    >
      <div ref={setCorps} className="min-h-0 flex-1 overflow-hidden">
        {lignes.length === 0 ? (
          <Vide taille="sm" titre="Personne d'autre pour l'instant." className="h-full" />
        ) : (
          <LayoutGroup>
            {visibles.map((l) => (
              <motion.div
                layout
                key={l.workosUserId}
                transition={{ type: "spring", stiffness: 420, damping: 32 }}
                style={{ height: LIGNE_PX }}
                className="overflow-hidden"
              >
                <RangCarte
                  variante="ligne"
                  dense
                  formater={dollarsUnites}
                  entree={{
                    ...entreeDe(l),
                    // La prime, en vert : c'est de l'argent gagné, pas une couleur de marque.
                    detail:
                      l.primeCents > 0 ? (
                        <span className="font-semibold text-[color:var(--color-valide)]">+{dollars(l.primeCents)} de prime</span>
                      ) : undefined,
                  }}
                />
              </motion.div>
            ))}
          </LayoutGroup>
        )}
      </div>
      {pages > 1 && (
        <Pagination page={page} pages={pages} onPage={setChoisie} total={lignes.length} parPage={parPage} taille="sm" className="shrink-0 px-4 py-2.5" />
      )}
    </Section>
  );
}

// ─── La semaine dernière ─────────────────────────────────────────────────────

function SemaineDerniere({ gagnants }: { gagnants: ClassementData["semaineDerniere"] }) {
  return (
    <div className="flex items-center gap-1.5">
      <Etiquette>Semaine dernière</Etiquette>
      {/* Tout ce qu'il a gagné — commission de ses ventes + prime — son
          nombre de ventes, puis la prime seule (Kayla, 12-10-2026).
          Commission inconnue : la prime. */}
      {gagnants.map((g) => {
        const pastille = (
          <span className="regie-round flex items-center gap-1.5 bg-sunken/60 py-0.5 pl-0.5 pr-2">
            <Avatar nom={g.nom} url={g.avatarUrl} taille="xs" anneau={METAL[g.rang]} />
            <span className="max-w-[7rem] truncate text-[11px] font-medium text-fg">{g.nom}</span>
            {g.ventes !== null && (
              <span className="text-[10px] tabular-nums text-muted">
                {g.ventes} vente{g.ventes > 1 ? "s" : ""}
              </span>
            )}
            {g.commissionCents === null ? (
              <span className="text-[11px] font-semibold tabular-nums text-fg">+{dollars(g.montantCents)}</span>
            ) : (
              <>
                <span className="text-[11px] font-semibold tabular-nums text-fg">
                  {dollars(g.commissionCents + g.montantCents)}
                </span>
                <span className="text-[10px] tabular-nums text-muted">dont {dollars(g.montantCents)} de prime</span>
              </>
            )}
          </span>
        );
        const cle = `${g.rang}-${g.nom}`;
        return g.commissionCents === null ? (
          <span key={cle}>{pastille}</span>
        ) : (
          <Infobulle key={cle} texte={`Commission ${dollars(g.commissionCents)} + prime ${dollars(g.montantCents)}`}>
            {pastille}
          </Infobulle>
        );
      })}
    </div>
  );
}

// ─── L'avatar (les pastilles « semaine dernière » ; le podium et les lignes
//     ont leur tuile dans la librairie) ────────────────────────────────────

const TAILLES = {
  xs: "h-5 w-5 text-[9px]",
  sm: "h-7 w-7 text-xs",
  md: "h-11 w-11 text-base",
  lg: "h-14 w-14 text-lg",
} as const;

export function Avatar({
  nom,
  url,
  taille,
  anneau,
}: {
  nom: string | null;
  url: string | null;
  taille: keyof typeof TAILLES;
  anneau?: string;
}) {
  const [casse, setCasse] = useState(false);
  const initiale = useMemo(() => (nom?.trim()[0] ?? "?").toUpperCase(), [nom]);
  return (
    <span
      className={cn(
        "regie-round flex shrink-0 items-center justify-center overflow-hidden bg-sunken font-semibold text-muted",
        TAILLES[taille],
        !nom && "border border-dashed border-line",
      )}
      style={anneau && nom ? { boxShadow: `0 0 0 2px ${anneau}` } : undefined}
    >
      {url && !casse ? (
        <img
          src={url}
          alt=""
          className="h-full w-full object-cover"
          referrerPolicy="no-referrer"
          onError={() => setCasse(true)}
        />
      ) : (
        initiale
      )}
    </span>
  );
}
